import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme, Box, TextField, IconButton, Typography } from "@material-ui/core";
import { observer } from "mobx-react-lite";
import { Toolbar, Button as Button } from "@roc/ui";
import { useStore } from "@roc/client-portal-shared/hooks";
import { LeadDetails } from "../../leadDetails/leadDetails";
import { BorrowerDetailedScreen } from "../../borrowerDetails/borrowerDetailedScreen";
import { LenderDetailedScreen } from "../../lenderDetails/lenderDetailedScreen";
import {LEAD_OBJECT_TYPE,BORROWER_OBJECT_TYPE,TPO_OBJECT_TYPE,BROKER_OBJECT_TYPE,} from "../../leads/constants/constants";
import { normalizeToE164 } from "apps/crm-portal/src/app/utils/phone";
import { useTwilioDevice } from "apps/crm-portal/src/app/hooks/useTwilioDevice";
import AutoNextDialModal from "apps/crm-portal/src/app/modules/dialSessions/AutoNextDialModal";
import DialControls from "apps/crm-portal/src/app/modules/dialSessions/DialControl";
import CadenceRecordList from "apps/crm-portal/src/app/modules/dialSessions/cadenceRecordList";
import { isProduction } from "@roc/feature-utils";
import Draggable from "react-draggable";
import CloseIcon from "@material-ui/icons/Close";
import UnsubscribeModal from "./unsubscribeModal";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    layout: {
      display: "flex",
      width: "100%",
      height: "80vh",
      border: "1px solid #ccc",
      overflow: "hidden",
    },
    details: {
      flex: 1,
      padding: "20px",
      overflowY: "auto",
      height: "100%",
    },
    noSelectionMessage: {
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "#666",
      fontSize: "1.1rem",
    },
  }),
);
type CallStepsDialerProps = {
  currentStep: any;
};

export const DialerCallStepsDue: React.FC<CallStepsDialerProps> = observer(
  ({ currentStep }) => {
  const classes = useStyles();
  const { salesforceLeadStore, cadenceStore, userStore } = useStore();
  const {dialSession} = cadenceStore;


    const [selectedProspectIds, setSelectedProspectIds] = useState(
      new Set<string>(),
    );

  const [openNote, setOpenNote] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [noteSize, setNoteSize] = useState({ width: 720, height: 400 });
  const [unsubscribeOpen, setUnsubscribeOpen] = useState(false);
  const [visibleRecords, setVisibleRecords] = useState(dialSession.records);

  const identity = userStore?.userInformation?.username?.trim() || "";
    const from = isProduction ? normalizeToE164(
      String(userStore?.userInformation?.workphone ?? "")) : "Testing";
    const twilio = useTwilioDevice({
      identity,
      from,
    });

  const handleCompleted = () => {
    const ids = Array.from(selectedProspectIds);
    cadenceStore.completeCallStepForCadenceContacts(ids);
    setVisibleRecords(prev => prev.filter(r => !ids.includes(r.cadenceContactInfo?.cadencesContactDataId)));

    setSelectedProspectIds(new Set<string>());
  };

  const handleSkip = () => {
    dialSession.markAsSkipped(Array.from(selectedProspectIds));
    setSelectedProspectIds(new Set<string>());
  };

  const handleMarkUnsubscribed = (reason?: string) => {
    const ids = Array.from(selectedProspectIds);
    cadenceStore.markCadenceContactAsUnsubscribed(ids, reason);
    setVisibleRecords(prev => prev.filter(r => !ids.includes(r.cadenceContactInfo?.cadencesContactDataId)));

    setSelectedProspectIds(new Set<string>());
  };

  useEffect(() => {
    dialSession.handleCallEnd(twilio.status, cadenceStore);
  }, [twilio.status]);

  useEffect(() => {
    dialSession.resetSelectedPerson();
  }, []);

  useEffect(() => {
    setVisibleRecords(dialSession.records);
  }, [dialSession.records]);

  
  const renderDetails = () => {
    if (!dialSession.selectedPerson)
        return (
          <Box className={classes.noSelectionMessage}>
            Please select a prospect to view details
          </Box>
        );

    const recordType = dialSession.selectedPerson?.recordType;
      const { leadId, contactId, entityId } =
        dialSession.selectedPerson?.cadenceContactInfo || {};

    switch (recordType?.toUpperCase()) {
      case LEAD_OBJECT_TYPE:
        return (
        <LeadDetails
          key={leadId}
          salesforceLeadStore={salesforceLeadStore}
          id={leadId}
          fromCadence
          />
      );
      case BORROWER_OBJECT_TYPE:
        return (
          <BorrowerDetailedScreen key={entityId} id={entityId} fromCadence />
      );
      case TPO_OBJECT_TYPE:
      case BROKER_OBJECT_TYPE:
        return (
        <LenderDetailedScreen
          key={entityId}
          id={entityId}
          fromCadence
          contactRecordType={recordType}
        />
      );
      default:
        return <Box>Unknown record type</Box>;
    }
  };

  return (
    <>
      <Toolbar toolbarContainerStyle={{ padding: "8px 0" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          px={2}
        >
          <Box display="flex" alignItems="center" ml={-2}>
            <Button
              testId=""
              variant="contained"
              size="small"
              color="primary"
              disabled={selectedProspectIds.size === 0}
              onClick={() => setUnsubscribeOpen(true)}
            >
              Unsubscribe
            </Button>
            <Button
              testId=""
              variant="contained"
              size="small"
              color="primary"
              disabled={selectedProspectIds.size === 0}
              style={{ marginLeft: 10 }}
              onClick={handleCompleted}
            >
              Mark Completed
            </Button>
            <Button
              testId=""
              variant="contained"
              size="small"
              color="default"
              disabled={selectedProspectIds.size === 0}
              onClick={handleSkip}
              style={{ marginLeft: 10 }}
            >
              Skip
            </Button>
          </Box>

          <Box display="flex" alignItems="center">
            <DialControls
              isMuted={twilio.isMuted}
              hasActiveCall={twilio.hasActiveCall}
              deviceReady={twilio.deviceReady}
              canDialCurrent={!!dialSession.selectedRecordId}
              canDialNext={!!dialSession.findNextDialable()}
              onToggleMute={twilio.toggleMute}
              onHang={twilio.hangup}
              onDialCurrent={() =>
                  dialSession.dialCurrent(
                    twilio,
                    userStore?.userInformation?.userId,
                  )
                }
                onDialNext={() =>
                  dialSession.dialNext(
                    twilio,
                    userStore?.userInformation?.userId,
                  )
                }
            />
          </Box>
        </Box>
      </Toolbar>

      <Box className={classes.layout}>
        <CadenceRecordList
          records={visibleRecords}
          selectedPerson={dialSession.selectedPerson}
          selectedProspectIds={selectedProspectIds}
          onSelectPerson={(p) => {
            const id = p?.cadenceContactInfo?.cadencesContactDataId;
            dialSession.selectPerson(p);

            setSelectedProspectIds((prev) => {
              const newSet = new Set(prev);
              if (newSet.has(id)) {
                 newSet.delete(id);
              } else {
                newSet.clear();
                newSet.add(id);
              }
              return newSet;
            });
          }}
          onToggleCheck={(id, checked) => {
            setSelectedProspectIds((prev) => {
              const newSet = new Set(prev);
              if (checked) newSet.add(id);
              else newSet.delete(id);
              return newSet;
            });
          }}
          activeRecordId={dialSession.activeCallId}
          activeStatus={twilio.status}
          title="Cadence Prospects"
          isCompleted={(id) => dialSession.isCompleted(id)}
          isSkipped={(id) => dialSession.isSkipped(id)}
        />
        <Box className={classes.details}>{renderDetails()}</Box>
      </Box>

      <AutoNextDialModal
        open={dialSession.autoModalOpen}
        leadName={dialSession.getName(dialSession.nextCandidateId)}
        leadPhone={dialSession.getE164(dialSession.nextCandidateId)}
        seconds={5}
        onTimeout={() =>
            dialSession.handleAutoTimeout(
              twilio,
              userStore?.userInformation?.userId,
            )
          }
        onPause={() => dialSession.closeAutoModal()}
      />

      <UnsubscribeModal
        open={unsubscribeOpen}
        onClose={() => setUnsubscribeOpen(false)}
        onConfirm={(reason) => handleMarkUnsubscribed(reason)}
      />

      {openNote && (
        <Draggable handle=".note-header">
          <Box
            style={{
              position: "fixed",
              top: "120px",
              left: "120px",
              width: noteSize.width,
              height: noteSize.height,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              zIndex: 2000,
              display: "flex",
              flexDirection: "column",
              resize: "both",
              overflow: "hidden"
            }}
          >
            <Box
              className="note-header"
              style={{
                cursor: "move",
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #eee",
              }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Notes
              </Typography>
              <IconButton size="small" onClick={() => setOpenNote(false)}>
                <CloseIcon fontSize="small"/>
              </IconButton>
            </Box>

            <TextField
              multiline
              variant="outlined"
              fullWidth
              value={currentStep?.cadenceStepsInfo?.stepContent?.content}
              style={{
                flex: 1,
                padding: "10px",
                border: "none",
                outline: "none",
                height: "calc(100% - 48px)",
              }}
              InputProps={{
                readOnly: true,
                style: {
                  height: "100%",
                  alignItems: "flex-start",
                }
              }}
            />
          </Box>
        </Draggable>
      )}

      <IconButton
        onClick={() => setOpenNote(true)}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          backgroundColor: "#1976d2",
          color: "#fff",
          width: "48px",
          height: "48px",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.3)",
          borderRadius: "50%"
        }}
      >
        📝
      </IconButton>
    </>
  );
});
